import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApolloError } from "@apollo/client";
import { Button } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import useAstradaLinkedAccounts from "hooks/useAstradaLinkedAccounts";
import { getGraphqlErrorMessage } from "utils/graphql";
import { AstradaConnection, EnrolledCard } from "./types";
import ConnectionsSection from "./ConnectionsSection";
import EnrolledCardsTable from "./EnrolledCardsTable";
import ManageConnectionDrawer from "./ManageConnectionDrawer";
import DisconnectProgramDialog from "./DisconnectProgramDialog";
import CardDetailDrawer from "./CardDetailDrawer";
import LoadingModal from "components/common/LoadingModal";

import httpClient from "utils/httpClient";
import { useAuth } from "@frontegg/react";
import { IntegrationType } from "enums/IntegrationType";
import { PageContainer, toast } from "@outpave/ds";

/** The server's message when there is one, so callers can fall back to their own. */
const readErrorMessage = (error: unknown): string =>
  error instanceof ApolloError && error.graphQLErrors.length > 0
    ? getGraphqlErrorMessage(error.graphQLErrors)
    : "";

/** Linked Accounts for companies on Astrada. See README.md. */
const AstradaLinkedAccounts: React.FC = () => {
  const { t } = useTranslation();

  const {
    connections,
    cards,
    assignCardUser,
    reenrollCard,
    connectionsLoading,
    connectionsError,
    refetchAll,
    cardsLoading,
    disconnectProgram,
    disconnecting,
    enrolling,
    astradaExternalAccount,
    handleUpdateAstradaConnection,
    updatingAstradaConnection,
    handleConnectIntegration,
    loadingConnection,
    loadingUnassignedCard,
    unassignedCards,
  } = useAstradaLinkedAccounts();

  // The page owns which connection is open; the drawer and dialog own nothing
  // but their own rendering. `manageOpen` is separate from the id so the id can
  // survive until the slide-out finishes, otherwise both surfaces would lose
  // their content mid-animation.
  const [manageConnectionId, setManageConnectionId] = useState<string | null>(
    null,
  );
  const [manageOpen, setManageOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  // Retained so the surfaces keep their content while they animate out. A
  // successful disconnect refetches before it resolves, so by the time the
  // drawer is told to close its connection is already out of `connections`.
  const lastManagedConnection = useRef<AstradaConnection | null>(null);

  const managedConnection = useMemo(() => {
    const foundConnection =
      connections.find((connection) => connection.id === manageConnectionId) ??
      null;

    if (foundConnection) lastManagedConnection.current = foundConnection;

    return foundConnection ?? lastManagedConnection.current;
  }, [connections, manageConnectionId]);

  const handleManage = (connectionId: string) => {
    setManageConnectionId(connectionId);
    setManageOpen(true);
  };

  const handleAfterClose = () => {
    setManageConnectionId(null);
    lastManagedConnection.current = null;
  };

  // Same retain-through-the-animation shape as the connection above.
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const lastSelectedCard = useRef<EnrolledCard | null>(null);

  const foundCard = useMemo(() => {
    return (
      [...cards, ...unassignedCards].find(
        (card) => card.id === selectedCardId,
      ) ?? null
    );
  }, [cards, unassignedCards, selectedCardId]);
  if (foundCard) lastSelectedCard.current = foundCard;
  const selectedCard = foundCard ?? lastSelectedCard.current;

  const handleSelectCard = (cardId: string) => {
    setSelectedCardId(cardId);
    setCardOpen(true);
  };

  const handleCardAfterClose = () => {
    setSelectedCardId(null);
    lastSelectedCard.current = null;
  };

  const handleDisconnect = async () => {
    if (!managedConnection) return;

    try {
      await disconnectProgram(managedConnection.id);
      setDisconnectOpen(false);
      setManageOpen(false);
      toast.success(t("ProgramDisconnected"));
    } catch (error) {
      // Both surfaces are left open so the admin can retry in place.
      toast.error(readErrorMessage(error) || t("Please try again!"));
    }
  };

  const handleShowActive = (connection: AstradaConnection) => {
    if (
      connection.astradaCard?.enrollmentId &&
      connection.astradaCard?.network
    ) {
      window.UnifiedEnrollmentSdk.open({
        cardEnrollment: false,
        bulkEnrollment: "forced",
        bulkFlow: "kickoff",
        bankEnrollment: "on",
        minBinDigits: 8,
        getAccessTokenTimeoutMs: 15000,
        companyName: astradaExternalAccount?.companyName,
        subaccountId: astradaExternalAccount?.externalId,
        getAccessToken: getAstradaToken,
        onEvent: (event: any) => console.log(event),
        onSuccess: (data: any) => {
          if (Object.keys(data.bulkFeeds).length > 0) {
            handleUpdateAstradaConnection(
              connection.id,
              connection.astradaCard?.enrollmentId || "",
              Object.keys(data.bulkFeeds)[0],
            );
          }
        },
        onError: (error: any) => {
          toast.error(t("Failed to connect Astrada Account"));
        },
        resume: {
          networkBulkEnrollmentId: connection.astradaCard?.enrollmentId,
          network: connection.astradaCard?.network,
        },
      });
    }
  };

  const ready = useUnifiedEnrollmentSdk();
  const { user } = useAuth();

  const getAstradaToken = async () => {
    const accessToken = user?.accessToken;

    const response = await httpClient.get("/api/v1/astrada/access-token", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response.data.linkToken;
  };

  const connectIntegration = async (type: string, key: string) => {
    try {
      const response = await handleConnectIntegration(type, key);

      if (response?.data?.connectIntegration) {
        toast.success(t("Enroll Astrada Program successfully"));
      } else {
        toast.error(t("Unable to enroll Astrada Program"));
      }
    } catch (error) {
      toast.error(t("Unable to enroll Astrada Program"));
    }
  };

  const handleAstradaLinkedButtonClick = () => {
    window.UnifiedEnrollmentSdk.open({
      cardEnrollment: false,
      bulkEnrollment: "forced",
      bulkFlow: "kickoff",
      bankEnrollment: "on",
      minBinDigits: 8,
      getAccessTokenTimeoutMs: 15000,
      companyName: astradaExternalAccount?.companyName,
      subaccountId: astradaExternalAccount?.externalId,
      getAccessToken: getAstradaToken,
      onEvent: (event: any) => console.log(event),
      onSuccess: (data: any) => {
        if (Object.keys(data.bulkEnrollments).length > 0) {
          connectIntegration(
            IntegrationType.ASTRADA_CONNECTION,
            Object.keys(data.bulkEnrollments)[0],
          );
        }
        if (Object.keys(data.bankAccounts).length > 0) {
          connectIntegration(
            IntegrationType.ASTRADA_BANK_CONNECTION,
            Object.keys(data.bankAccounts)[0],
          );
        }
      },
      onError: (error: any) => {
        toast.error(t("Failed to connect Astrada Account"));
      },
    });
  };

  return (
    <PageContainer
      wrapped={false}
      title={t("Linked Accounts")}
      subtitle={t("LinkedAccountsAstradaDescription")}
      primaryAction={
        astradaExternalAccount?.externalId ? (
          <Button
            variant="primary-filled"
            disabled={!ready}
            onClick={handleAstradaLinkedButtonClick}
          >
            {t("Enroll program")}
          </Button>
        ) : undefined
      }
    >
      {/* Fills PageContainer so the cards section can take the leftover height. See README.md. */}
      <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
        <ConnectionsSection
          connections={connections}
          loading={connectionsLoading}
          error={Boolean(connectionsError)}
          isDisabledEnroll={!ready}
          onRetry={refetchAll}
          onManage={handleManage}
          onActive={handleShowActive}
          onEnroll={handleAstradaLinkedButtonClick}
        />

        {connections.length > 0 && (
          <EnrolledCardsTable
            unAssignedCards={unassignedCards}
            cards={cards}
            loading={cardsLoading || loadingUnassignedCard}
            onSelectCard={handleSelectCard}
          />
        )}

        <ManageConnectionDrawer
          open={manageOpen}
          connection={managedConnection}
          onClose={() => setManageOpen(false)}
          onAfterClose={handleAfterClose}
          onDisconnect={() => setDisconnectOpen(true)}
        />

        <CardDetailDrawer
          open={cardOpen}
          card={selectedCard}
          onClose={() => setCardOpen(false)}
          onAfterClose={handleCardAfterClose}
          onAssignUser={assignCardUser}
          onReenroll={reenrollCard}
        />

        <DisconnectProgramDialog
          open={disconnectOpen}
          connection={managedConnection}
          onClose={() => setDisconnectOpen(false)}
          onConfirm={handleDisconnect}
          disconnecting={disconnecting}
        />

        <LoadingModal
          open={
            connectionsLoading ||
            enrolling ||
            disconnecting ||
            loadingUnassignedCard ||
            loadingConnection ||
            updatingAstradaConnection
          }
        />
      </Stack>
    </PageContainer>
  );
};

const SDK_SRC = process.env.REACT_APP_ASTRADA_UNIFIED_ENROLLMENT_SDK_SRC || "";
const ASTRADA_ENV = process.env.REACT_APP_ASTRADA_ENV || "";

export function useUnifiedEnrollmentSdk(): boolean {
  const [ready, setReady] = useState(() => !!window.UnifiedEnrollmentSdk);

  useEffect(() => {
    if (window.UnifiedEnrollmentSdk) return setReady(true);

    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.dataset.id = "unified-enrollment-sdk";
    if (ASTRADA_ENV === "SANDBOX") {
      script.dataset.unifiedEnrollmentSdkEnv = "sandbox";
    }
    script.addEventListener("load", () => setReady(true));
    document.body.appendChild(script);
  }, []);

  return ready;
}

export default AstradaLinkedAccounts;
